import React from 'react';
import type { PlaybookEntity } from '../types/playbook.types';

interface PlaybookGuardsTabProps {
  playbook: PlaybookEntity;
}

const PlaybookGuardsTab: React.FC<PlaybookGuardsTabProps> = ({ playbook }) => {
  const transitions = playbook.transitions || [];

  const guardsPerTransition = transitions
    .filter((t) => (t.guards?.length || 0) > 0 || (t.validators?.length || 0) > 0)
    .sort((a, b) => a.name.localeCompare(b.name));

  if (guardsPerTransition.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
        No guards or validators configured for any transition.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {guardsPerTransition.map((t) => (
        <div key={t.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <h4 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">
            {t.name} <span className="text-gray-400 font-normal">({t.slug})</span>
          </h4>

          {/* Guards */}
          {t.guards && t.guards.length > 0 && (
            <div className="mb-2">
              <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Guards ({t.guards.length})
              </span>
              <div className="mt-1 space-y-1">
                {t.guards.map((g) => (
                  <div key={g.id} className="flex items-start gap-2 py-1 px-2 rounded bg-gray-50 dark:bg-gray-800/50">
                    <span className="inline-flex items-center rounded bg-lex-accent-soft px-1.5 py-0.5 text-[9px] font-medium text-lex-accent">
                      {g.guard_type}
                    </span>
                    {g.error_message && (
                      <span className="text-[10px] text-gray-500 dark:text-gray-400">{g.error_message}</span>
                    )}
                    {Object.keys(g.configuration).length > 0 && (
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">
                        {JSON.stringify(g.configuration)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Validators */}
          {t.validators && t.validators.length > 0 && (
            <div>
              <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Validators ({t.validators.length})
              </span>
              <div className="mt-1 space-y-1">
                {t.validators.map((v) => (
                  <div key={v.id} className="flex items-start gap-2 py-1 px-2 rounded bg-gray-50 dark:bg-gray-800/50">
                    <span className="inline-flex items-center rounded bg-teal-100 px-1.5 py-0.5 text-[9px] font-medium text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
                      {v.validator_type}
                    </span>
                    {v.error_message && (
                      <span className="text-[10px] text-gray-500 dark:text-gray-400">{v.error_message}</span>
                    )}
                    {Object.keys(v.configuration).length > 0 && (
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">
                        {JSON.stringify(v.configuration)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default PlaybookGuardsTab;
